import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, List } from "lucide-react";

const ora = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "");
const giorno = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");

const Riga = ({ label, valore }) => (
  <p className="flex justify-between gap-3">
    <span className="uppercase tracking-wide text-neutral-500">{label}</span>
    <span className="font-semibold">{valore}</span>
  </p>
);

/** Blocco di un giro, come sul blocchetto: le righe una sotto l'altra e il timbro dell'invio. */
const Giro = ({ n, righe, eur, vedeImporti }) => (
  <div className="mt-4" data-testid={`foglio-giro-${n}`}>
    <p className="flex items-baseline justify-between border-b border-dashed border-neutral-400 pb-1">
      <span className="font-bold">GIRO {n}</span>
      {righe.some((i) => i.sent) ? (
        <span className="text-[11px] uppercase tracking-wide text-neutral-500">
          mandato {ora(righe.find((i) => i.sent_at)?.sent_at)}
        </span>
      ) : (
        <span className="rounded border border-neutral-400 px-1.5 text-[11px] font-bold uppercase tracking-wide">
          da mandare
        </span>
      )}
    </p>
    {righe.map((i) => (
      <div key={i.id} className={`mt-1.5 leading-tight ${i.voided ? "line-through opacity-50" : ""}`} data-testid={`foglio-riga-${i.id}`}>
        <p className="flex gap-2">
          <span className="w-8 shrink-0 font-bold">{i.qty}×</span>
          <span className="flex-1">{i.name}</span>
          <span className="shrink-0 text-neutral-500">{(i.department || "").slice(0, 3).toUpperCase()}</span>
          {vedeImporti && <span className="w-16 shrink-0 text-right">{eur((i.price + (i.surcharge || 0)) * i.qty)}</span>}
        </p>
        {(i.removed || []).length > 0 && (
          <p className="ml-10 text-[12px] font-bold uppercase text-neutral-900" data-testid={`foglio-senza-${i.id}`}>
            senza {i.removed.join(", ")}
          </p>
        )}
        {(i.note || i.cooking || i.grams > 0 || i.surcharge_label || i.allergens?.length > 0 || i.extras?.length > 0) && (
          <p className="ml-10 text-[12px] text-neutral-600">
            {[i.cooking, i.grams > 0 ? `${i.grams} g` : "",
              i.surcharge_label || (i.extras || []).map((e) => e.name || e).join(" · "),
              i.note, i.allergens?.length ? `allergeni: ${i.allergens.join(", ")}` : ""]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}
      </div>
    ))}
  </div>
);

/**
 * La comanda come il foglio del blocchetto: si scrive col pollice, si legge come su carta.
 * Sola lettura: qui non si tocca nulla, si rilegge al tavolo, si mostra al cliente, si stampa.
 */
export const FoglioComanda = ({ order, open, onClose, eur, vedeImporti, locale, onLista, preferita, setPreferita, stampa = true }) => {
  if (!order) return null;
  const righe = [...order.items].sort((a, b) => (a.giro || 1) - (b.giro || 1));
  const giri = [...new Set(righe.map((i) => i.giro || 1))].sort((a, b) => a - b);
  const asporto = order.mode === "asporto";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="foglio-dialog">
        <DialogHeader className="sr-only">
          <DialogTitle>Foglio comanda</DialogTitle>
        </DialogHeader>

        <div id="foglio-stampa" className="rounded-sm bg-[#fdfbf4] p-5 font-mono text-[13px] text-neutral-900 shadow-inner">
          <style>{`@media print {
            body * { visibility: hidden !important; }
            #foglio-stampa, #foglio-stampa * { visibility: visible !important; }
            #foglio-stampa { position: fixed; inset: 0; box-shadow: none; background: #fff; }
          }`}</style>
          <p className="text-center text-[11px] font-bold uppercase tracking-[0.3em]">{locale || "Comanda"}</p>
          <p className="mt-0.5 text-center text-[11px] text-neutral-500">
            {giorno(order.created_at)} · {ora(order.created_at)}
          </p>
          <div className="mt-3 space-y-0.5 border-y border-dashed border-neutral-400 py-2">
            <Riga
              label={asporto ? "Asporto" : "Tavolo"}
              valore={asporto ? order.table_numbers[0] : order.group_name || order.table_numbers.join(" + ")}
            />
            {asporto ? (
              <>
                {order.customer_name && <Riga label="Cliente" valore={order.customer_name} />}
                {order.pickup_time && <Riga label="Ritiro" valore={order.pickup_time} />}
                {order.rider && <Riga label="Rider" valore={order.rider} />}
              </>
            ) : (
              <Riga label="Coperti" valore={order.covers} />
            )}
            <Riga label="Cameriere" valore={order.waiter} />
          </div>

          {giri.map((n) => (
            <Giro key={n} n={n} righe={righe.filter((i) => (i.giro || 1) === n)} eur={eur} vedeImporti={vedeImporti} />
          ))}

          {vedeImporti && (
            <div className="mt-4 space-y-0.5 border-t-2 border-double border-neutral-500 pt-2">
              <Riga label="Piatti" valore={eur(order.subtotal)} />
              {order.cover_total > 0 && <Riga label="Coperto" valore={eur(order.cover_total)} />}
              {order.discount_total > 0 && <Riga label="Sconto" valore={`− ${eur(order.discount_total)}`} />}
              <Riga label="Totale" valore={eur(order.total)} />
              {order.paid > 0 && <Riga label="Incassato" valore={eur(order.paid)} />}
              {order.paid > 0 && <Riga label="Resta" valore={eur(order.remaining)} />}
            </div>
          )}
          <p className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-neutral-400">
            {order.items.length} righe · comanda {order.id.slice(0, 8)}
          </p>
        </div>

        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={preferita}
            onChange={(e) => setPreferita(e.target.checked)}
            data-testid="foglio-preferita"
          />
          Apri sempre il foglio quando tocco «già ordinato»
        </label>

        <div className="flex flex-wrap gap-2">
          {stampa && (
            <Button className="h-12 flex-1" onClick={() => window.print()} data-testid="foglio-print-btn">
              <Printer className="mr-1.5 h-4 w-4" /> Stampa
            </Button>
          )}
          <Button variant="outline" className={stampa ? "h-12" : "h-12 flex-1"} onClick={onLista} data-testid="foglio-lista-btn">
            <List className="mr-1.5 h-4 w-4" /> Vista elenco
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
